import { useMemo, useState } from "react";
import { ArrowLeft, Globe, Loader2, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLanguageOptions, type PickerOption } from "@/lib/liveavatar/options";
import { useTavusLanguages } from "@/lib/scenarios/providers/tavus/languages";
import { useKalturaVoices } from "@/lib/scenarios/providers/kaltura/options";
import type { Scenario } from "@/lib/powtoon-be/types";
import { withCacheBust } from "@/lib/utils";

interface Props {
  scenario: Scenario;
  connecting: boolean;
  onStart: (language: string) => void;
  onBack?: () => void;
}

/**
 * Which providers accept a language at session start. D-ID bakes the
 * language into the agent's voice at save time, so the learner has no
 * runtime choice there.
 */
const RUNTIME_LANGUAGE_PROVIDERS = new Set(["liveavatar", "tavus", "kaltura"]);

/**
 * Provider-aware pre-session screen. The language dropdown is populated
 * from the same catalog the trainer used in the editor (LiveAvatar,
 * Tavus, or Kaltura), so the value shipped to `onStart(lang)` is always a
 * valid id for the runtime that consumes it.
 */
export function PreSessionScreen({ scenario, connecting, onStart, onBack }: Props) {
  const provider = scenario.content?.provider ?? "liveavatar";
  const showLanguagePicker = RUNTIME_LANGUAGE_PROVIDERS.has(provider);
  // Every provider's language hook is called unconditionally to keep
  // hook order stable; unused ones are cheap (static list or cached
  // query gated by `enabled`).
  const laLangs = useLanguageOptions();
  const tvLangs = useTavusLanguages({ enabled: provider === "tavus" });
  const kalVoices = useKalturaVoices({ enabled: provider === "kaltura" });
  const kalLangs = useMemo<PickerOption[]>(
    () =>
      kalVoices.languages.map((l) => {
        let label = l;
        try {
          const dn = new Intl.DisplayNames(["en"], { type: "language" });
          label = dn.of(l.split(/[-_]/)[0]) || l;
        } catch {
          /* ignore */
        }
        return { id: l, label };
      }),
    [kalVoices.languages],
  );
  const languages: PickerOption[] =
    provider === "tavus"
      ? tvLangs.options
      : provider === "kaltura"
        ? kalLangs
        : laLangs.options;

  // Preselect the pal / scenario language, then the provider's own
  // English match, then the first option, then a last-resort "en".
  const initialLanguage = useMemo(() => {
    const saved = scenario.content?.language;
    if (saved && languages.some((l) => l.id === saved)) return saved;
    // Tavus stores plain "English"; LiveAvatar and D-ID use BCP-47 ids
    // like `en-US`. Match either.
    const enRe = provider === "tavus" ? /^en(glish)?$/i : /^en(-|$)/i;
    const en = languages.find((l) => enRe.test(l.id));
    if (en) return en.id;
    if (languages[0]) return languages[0].id;
    return "en";
  }, [scenario.content?.language, languages, provider]);
  const [language, setLanguage] = useState(initialLanguage);
  // If options load after mount and our current pick isn't in the list,
  // snap to the resolved default so the Select shows a valid label.
  const currentValid = languages.some((l) => l.id === language);
  const displayValue = currentValid ? language : initialLanguage;

  return (
    <div className="relative flex min-h-[100dvh] flex-col items-center justify-center bg-background px-6 pb-32">
      {onBack && (
        <Button
          onClick={onBack}
          size="icon"
          variant="ghost"
          className="absolute top-4 left-4 rounded-lg"
          aria-label="Back"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
      )}
      <div className="flex max-w-sm flex-col items-center text-center space-y-6">
        <div className="h-28 w-28 rounded-3xl bg-accent border border-border overflow-hidden">
          {withCacheBust(scenario.thumb_url, scenario.modified) ? (
            <img
              src={withCacheBust(scenario.thumb_url, scenario.modified)}
              className="h-full w-full object-cover"
              alt={scenario.name}
            />
          ) : null}
        </div>
        <div>
          <p className="text-lg font-semibold text-foreground">{scenario.name}</p>
          {scenario.description && (
            <p className="mt-1 text-sm text-muted-foreground">{scenario.description}</p>
          )}
        </div>
        {showLanguagePicker && (
          <div className="w-full max-w-xs space-y-2 text-left">
            <label className="flex items-center gap-2 text-sm font-medium text-foreground">
              <Globe className="h-4 w-4 text-muted-foreground" />
              Language
            </label>
            <Select value={displayValue} onValueChange={setLanguage}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(languages.length > 0 ? languages : [{ id: "en", label: "English" }]).map((l) => (
                  <SelectItem key={l.id} value={l.id}>
                    {l.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {provider === "kaltura" && (
              <p className="text-xs text-muted-foreground">
                The voice was provisioned for this scenario's language — changing the language
                changes the spoken language, not the voice.
              </p>
            )}
          </div>
        )}
      </div>
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 w-full max-w-xs px-6">
        <Button
          onClick={() => onStart(displayValue)}
          disabled={connecting}
          size="lg"
          className="w-full"
        >
          {connecting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Starting session…
            </>
          ) : (
            <>
              <Play className="h-4 w-4" />
              Start session
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
